
import AppLayoutVue from "@/Layouts/AppLayout.vue";

import Order from "@/types/Order";
import {
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  computed,
  ref,
  watch,
  nextTick,
} from "@vue/runtime-core";
import Messages from "@/Components/General/Messages.vue";
import { useStore as useMainStore } from "@/stores/main";
import Textarea from "@/Components/Forms/Textarea.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import TextButton from "@/Components/Buttons/TextButton.vue";
import {
  mdiArrowLeft,
  mdiArrowULeftBottom,
  mdiCancel,
  mdiCartArrowUp,
  mdiCash,
  mdiCheckCircle,
  mdiCreditCardOutline,
  mdiCubeSend,
  mdiHome,
  mdiOpenInNew,
  mdiPlus,
  mdiClock,
  mdiMessageTextFast,
  mdiLockOpenVariant,
  mdiLock,
} from "@mdi/js";
import SvgIcon from "@jamescoyle/vue-icon";
import Button from "@/Components/Buttons/Button.vue";
import dayjs from "dayjs";
import CustomerSelect from "@/Components/Forms/CustomerSelect.vue";
import CashRegister from "@/types/CashRegister";
import Multiselect from "@/Components/General/Multiselect.vue";
import Payments from "@/Components/Order/Payments.vue";
import CashRegisterSelect from "@/Components/Order/CashRegisterSelect.vue";
import OrderLines from "@/Components/Order/OrderLines.vue";
import AddProduct from "@/Components/Order/AddProduct.vue";
import { useStore as useOrderStore } from "@/stores/order";
import PaymentInfo from "@/Components/Order/PaymentInfo.vue";
import Modal from "@/Components/General/Modal.vue";
import axios from "axios";
import CompletedActions from "@/Components/Order/CompletedActions.vue";
import { debounce } from "lodash";
import SaveDisplay from "@/Components/Forms/SaveDisplay.vue";
import { Inertia } from "@inertiajs/inertia";
import Comments from "@/Components/Order/Comments.vue";
import AddBags from "@/Components/Order/AddBags.vue";

export default defineComponent({
  methods: {
    mdiLock() {
      return mdiLock;
    },
    mdiLockOpenVariant() {
      return mdiLockOpenVariant;
    },
  },
  // layout: AppLayoutVue,
  props: {
    order: {
      type: Object as PropType<Order>,
      required: true,
    },
    already_opened: {
      type: Boolean,
      default: () => false,
    },
    cash_registers: {
      type: Object as PropType<CashRegister[]>,
      required: true,
    },
    statuses: {
      type: Object as PropType<string[]>,
      default: () => [],
    },
    wc_shipping_url: {
      type: String,
      required: true,
    },
    cash_back: {
      type: Number,
      required: false,
    },
  },
  components: {
    Messages,
    Textarea,
    TextButton,
    SvgIcon,
    Button,
    CustomerSelect,
    Multiselect,
    Payments,
    CashRegisterSelect,
    OrderLines,
    AddProduct,
    PaymentInfo,
    Modal,
    CompletedActions,
    SaveDisplay,
    Comments,
    AddBags,
  },
  setup(props) {
    const mainStore = useMainStore();
    mainStore.setUpStore();

    const orderStore = useOrderStore();
    orderStore.order = props.order;
    orderStore.order_id = props.order.id;
    orderStore.orderlines = props.order.orderlines;
    const showSMSModal = ref(false);

    const lockOrder = (orderId: any) => {
      Inertia.visit(route("orders.lock", { order: orderId }), {
        method: "post",
      } as any);
    };

    const unlockOrder = (orderId: any) => {
      Inertia.visit(route("orders.unlock", { order: orderId }), {
        method: "post",
      } as any);
    };

    onMounted(() => {
      console.log(`Mounted, opening order ${props.order.id}`);
      orderStore.openOrder(props.already_opened);
    });

    onUnmounted(() => {
      console.log(`Unmounted, closing order ${props.order.id}`);
      orderStore.closeOrder();
    });

    watch(
      () => props.order,
      (newValue) => {
        orderStore.switchOrder(newValue, props.already_opened);
      },
      {
        deep: true,
      }
    );

    const getOrderForm = () => {
      return useForm({
        _method: "PUT",
        status: props.order.status ?? "Mottatt",
        client: props.order.client,
        comment_order: props.order.comment_order,
        comment_invoice: props.order.comment_invoice,
        comment_client: props.order.client_model?.comment ?? null,
        customer_note: props.order.customer_note ?? "",
      });
    };

    const orderForm = ref(getOrderForm());

    const ignoreNextUpdate = ref(false);

    const sendOrderForm = debounce(() => {
      if (ignoreNextUpdate.value) {
        ignoreNextUpdate.value = false;
        return;
      }

      orderForm.value.post(route("orders.update", { order: props.order.id }), {
        preserveScroll: true,
        onSuccess: () => {
          orderForm.value.comment_client =
            props.order.client_model?.comment ?? null;

          if (orderForm.value.client !== props.order.client) {
            ignoreNextUpdate.value = true;
            orderForm.value.client = props.order.client;
          }
        },
      });
    }, 500);

    const sendOrder = () => {
      orderForm.value.status = "Sendt";

      sendOrderForm();
    };

    const archiveOrder = () => {
      Inertia.post(
        route("orders.destroy", { order: orderStore.order_id }),
        {
          _method: "DELETE",
        },
        {
          preserveScroll: true,
        }
      );
    };

    const cancelOrder = () => {
      orderForm.value.status = "Kansellert";

      sendOrderForm();
    };

    const undoComplete = () => {
      orderForm.value.status = "Mottatt";

      sendOrderForm();
    };

    onMounted(() => {
      mainStore.navIsSmall = true;
    });

    onUnmounted(() => {
      mainStore.navIsSmall = false;
    });

    const created_at_string = computed(() => {
      return dayjs(props.order.date).format("L LT");
    });

    const toCurrency = (price: number, round: boolean = false) => {
      const formattedPrice = price.toLocaleString("nb", {
        maximumFractionDigits: round ? 0 :2,

      });
      return `kr ${formattedPrice}`;
    };

    const lookAtAlreadyOpenOrder = ref(false);

    const orderIsEditable = computed(() => orderStore.isEditable);

    const isOpenInterval = setInterval(() => {
      if (!orderStore.openElsewhere) {
        axios.post(route("orders.open", { order: orderStore.order_id }));
      }
    }, 10000);

    onUnmounted(() => {
      clearInterval(isOpenInterval);
    });

    watch(
      () => orderStore.orderlines.length,
      (newValue, oldValue) => {
        if (newValue > oldValue) {
          scrollProductsToBottom();
        }
      }
    );

    watch(
      () => orderStore.loadingOrderlines.length,
      (newValue, oldValue) => {
        if (newValue > oldValue) {
          scrollProductsToBottom();
        }
      }
    );

    const scrollProductsToBottom = () => {
      nextTick(() => {
        const element = document.getElementById("productsScrollable");

        if (element) {
          element.scroll({ top: element.scrollHeight, behavior: "auto" });
        }
      });
    };

    const orderingShipping = ref(false);

    const homeLink = computed(() => {
      return route("home");
    });

    const orderListLink = computed(() => {
      if (props.order.weborder) {
        return "/cash-till/open-web-orders/";
      } else {
        return "/cash-till/open-orders";
      }
    });
    const back = () => {
      window.history.back();
    };

    //Sms sending form and submit section

    const smsForm = useForm({
      phone_number: props.order?.client_model?.phone,
      message: "Hei\n\nDin webordre er klar til henting hos oss.\n\nMVH HCPet",
    });
    const sendSMS = () => {
      smsForm.post(route("orders.sms", { order: props.order.id }), {
        preserveScroll: true,
        onSuccess: () => {
          showSMSModal.value = false;
          smsForm.reset();
        },
      });
    };

    return {
      smsForm,
      orderForm,
      sendOrderForm,
      sendOrder,
      cancelOrder,
      archiveOrder,
      undoComplete,
      toCurrency,
      sendSMS,
      mdiLock,
      mdiLockOpenVariant,
      homeLink,
      orderListLink,
      orderingShipping,
      lookAtAlreadyOpenOrder,
      created_at_string,
      orderIsEditable,
      isInvoiced: computed(() => orderStore.isInvoiced),
      homeIcon: mdiHome,
      plusIcon: mdiPlus,
      cardIcon: mdiCreditCardOutline,
      cashIcon: mdiCash,
      newIcon: mdiPlus,
      undoIcon: mdiArrowULeftBottom,
      cancelIcon: mdiCancel,
      externalIcon: mdiOpenInNew,
      orderSentIcon: mdiCubeSend,
      checkCircleIcon: mdiCheckCircle,
      leftArrowIcon: mdiArrowLeft,
      cartUpIcon: mdiCartArrowUp,
      clockIcon: mdiClock,
      createOrderUrl: route("orders.create"),
      isOpenElsewhere: computed(() => orderStore.openElsewhere),
      openElsewhereLocation: computed(() => orderStore.openLocation),
      isAdmin: computed(() => mainStore.isAdmin),
      payed: computed(() => orderStore.isPayed),
      invoiced: computed(() => orderStore.isInvoiced),
      isWeborder: computed(() => orderStore.isWeborder),
      orderTotalExMva: computed(() => orderStore.totalExMva),
      orderTotalIncMva: computed(() => orderStore.totalIncMva),
      smsIcon: mdiMessageTextFast,
      showSMSModal,
      back,
      lockOrder,
      unlockOrder,
    };
  },
});
